import React, { memo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Loading } from "app/components/Wrappers";
import edit_pen from "app/images/edit-pen.svg";
import trash from "app/images/trash.svg";
import styles from "./styles.module.scss";

interface IProps {
  initials: string;
  // Renders as the avatar photo when set; empty state shows the initials.
  imageUrl?: string | null;
  uploading?: boolean;
  // Called with the raw file; the parent owns the upload + resulting URL/id.
  onUpload: (file: File) => void;
  onDelete: () => void;
}

// The Coach Identity step's profile-photo control: a 96px rounded square with
// an initials fallback and one corner fab — pencil to pick a photo while
// empty, trash to remove it once filled.
const AvatarField = ({
  initials,
  imageUrl,
  uploading = false,
  onUpload,
  onDelete,
}: IProps) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const onFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      onUpload(file);
    }
    // Reset so re-selecting the same file fires change again.
    event.target.value = "";
  };

  const hasImage = !!imageUrl;

  return (
    <div className={styles.container}>
      <div
        className={styles.square}
        style={hasImage ? { backgroundImage: `url(${imageUrl})` } : undefined}
      >
        {uploading && <Loading isLoading loadType="spinner" />}
        {!uploading && !hasImage && initials}
      </div>
      <button
        type="button"
        className={styles.fab}
        disabled={uploading}
        aria-label={hasImage ? t("Delete") : t("Upload photo")}
        onClick={
          hasImage ? onDelete : () => fileInputRef.current?.click()
        }
      >
        <img src={hasImage ? trash : edit_pen} alt="" />
      </button>
      <input
        type="file"
        accept="image/*"
        className={styles.file_input}
        ref={fileInputRef}
        onChange={onFileChange}
      />
    </div>
  );
};

export default memo(AvatarField);
